import { CombinedError } from "@urql/core";
import { getClient } from "./api";
import { FeatureFlagsQueryDocument } from "./api/queries";
import { makeAttributes } from "./utils";
import { getUrlArrPairs } from "./utils/getUrlArrPairs";
import { makeFlagSchema } from "./utils/makeFlagSchema";
import { replaceFlagValue } from "./utils/replaceFlagValue";
/**
 * {@link FlagsSdk} singleton builder.
 *
 * @remarks
 * Do not use this directly. Use {@link FlagsSdk}, instead.
 *
 * @public
 */ export class FlagsSdkSingleton {
    static _instance;
    _authToken;
    /**
   * @internal
   */ _client;
    _environment;
    _flags;
    _flagsFromURL;
    _userKey;
    _contextKind;
    _name;
    _attributes;
    constructor(){
        if (FlagsSdkSingleton._instance) {
            // eslint-disable-next-line no-constructor-return
            return FlagsSdkSingleton._instance;
        }
        FlagsSdkSingleton._instance = this;
    }
    /**
   * Initialize the SDK with sane defaults.
   *
   * @param config - configuration object defined by {@link FlagsSdkConfig}
   */ init(config) {
        this._authToken = config.authToken;
        this._environment = config.environment || 'PROD';
        this._userKey = config.userKey;
        this._contextKind = config.contextKind || 'user';
        this._name = config.name;
        this._attributes = makeAttributes(config);
        this._initializeClient();
        this._getFlagsFromURL();
        return this;
    }
    /**
   * Initialize GQL client based on class state.
   */ _initializeClient() {
        if (!this._environment) {
            throw Error('Environment not defined.');
        }
        if (!this._name) {
            throw Error('Client name not defined.');
        }
        if (!this._authToken) {
            throw Error('Authentication token not defined');
        }
        this._client = getClient({
            authToken: this._authToken,
            environment: this._environment,
            name: this._name
        });
    }
    /**
   * Handle flag overrides from URL
   */ _getFlagsFromURL() {
        const queryString = window.location.search;
        const hasFlagOverride = queryString.includes('_f');
        if (!queryString || !hasFlagOverride) {
            return this;
        }
        this._flagsFromURL = getUrlArrPairs(queryString, '_f');
    }
    /**
   * Sets attributes for flags
   *
   * @example
   * ```ts
   * const flagsSdk = FlagsSdk.init({});
   * flagsSdk.attributes({ customAttributes: { clientId: 'mgm-web' } });
   * ```
   */ attributes(attributes) {
        this._attributes = attributes;
        return this;
    }
    /**
   * Sets the flags to fetch.
   *
   * @example
   * ```ts
   * const flagsSdk = FlagsSdk.init({});
   * flagsSdk.flags(['enable-feature-1', 'disable-feature-2']);
   * ```
   */ flags(xs) {
        this._flags = xs;
        return this;
    }
    /**
   * Sets the unique identifier for a user.
   *
   * @remarks
   * See {@link fetchUserKey}
   */ userKey(x) {
        this._userKey = x;
        return this;
    }
    /**
   * Sets the API environment and re-initializes the GQL client.
   */ environment(x) {
        this._environment = x;
        this._initializeClient();
        return this;
    }
    /**
   * Re-initializes the GQL client with a new authorization token.
   *
   * @remarks
   * TODO: this should be possible to handle internally, removing dependency on an explicitly provided
   * token.
   */ authToken(authToken) {
        this._authToken = authToken;
        this._initializeClient();
        return this;
    }
    /**
   * Returns configuration object defining SDK behavior.
   */ get debug() {
        return {
            attributes: this._attributes,
            authToken: this._authToken,
            contextKind: this._contextKind,
            environment: this._environment,
            flags: this._flags,
            flagsFromURL: this._flagsFromURL,
            userKey: this._userKey
        };
    }
    /**
   * Fetch feature flags.
   *
   * @example
   * ```ts
   * FlagsSdk
   *   .init({
   *    authToken: 'authToken',
   *    environment: 'PROD',
   *    name: 'name',
   *    contextKind: 'user
   *   })
   *   .attributes({
   *    customAttributes: {
   *     clientId: 'mgm-web'
   *    }
   *   })
   *   .flags([
   *     'enable-feature-1',
   *     'disable-feature-2',
   *   ])
   *   .fetch();
   * ```
   */ async fetch() {
        if (!this._client) {
            throw Error('SDK is not initialized');
        }
        if (!this._userKey) {
            throw Error('User key is not defined');
        }
        return this._client.query(FeatureFlagsQueryDocument, {
            attributes: this._attributes,
            userKey: this._userKey,
            contextKind: this._contextKind
        }).toPromise().then((v)=>v.data?.featureFlagVariationDetails).then((xs)=>{
            const enrichedFlags = this._flagsFromURL ? replaceFlagValue(xs, makeFlagSchema(this._flagsFromURL)) : xs;
            if (this._flags) {
                return enrichedFlags?.filter((v)=>v && this._flags?.includes(v.flagKey));
            }
            return enrichedFlags;
        }).catch((e)=>{
            if (e instanceof CombinedError) {
                throw Error(e.message);
            } else {
                throw e;
            }
        });
    }
}
/**
 * FlagsSdk singleton instance.
 *
 * @see {@link https://mgmresorts.github.io/client-feature-flagging}
 *
 * @public
 */ export const FlagsSdk = new FlagsSdkSingleton();
